import {
  ArrowUpRight,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  Flame,
  MapPin,
  Search,
  Sparkles,
  UserCheck,
  UsersRound,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCourseSignups, getCourses, updateCourseSignup } from '../../firebase/firestore';
import { useTenant } from '../../hooks/useTenant';
import { formatDateTime } from '../../utils/date';

function formatStudentName(item) {
  return item?.fullName || `${item?.firstName || ''} ${item?.lastName || ''}`.trim() || 'Unnamed applicant';
}

function getInitials(name = '') {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function sameDay(firstDate, secondDate) {
  return firstDate
    && secondDate
    && firstDate.getFullYear() === secondDate.getFullYear()
    && firstDate.getMonth() === secondDate.getMonth()
    && firstDate.getDate() === secondDate.getDate();
}

const leadStatuses = ['Neutral', 'Hot', 'Cold'];

function normalizeLeadStatus(status) {
  return normalizeText(status) || 'neutral';
}

function getNextLeadStatus(current) {
  const normalized = String(current || 'Neutral');
  const index = leadStatuses.indexOf(normalized);
  const nextIndex = index === -1 ? 0 : (index + 1) % leadStatuses.length;
  return leadStatuses[nextIndex];
}

function statusTone(status) {
  const normalized = normalizeLeadStatus(status);
  if (normalized === 'hot') return 'hot';
  if (normalized === 'cold') return 'cold';
  return 'neutral';
}

export function ApplicationsPage() {
  const { activeTenantId } = useTenant();
  const [applications, setApplications] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourse, setFilterCourse] = useState('All');
  const [filterCountry, setFilterCountry] = useState('All');
  const [filterLead, setFilterLead] = useState('All');
  const [savingLeadId, setSavingLeadId] = useState('');

  const load = useCallback(async () => {
    if (!activeTenantId) {
      setApplications([]);
      setCourses([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [nextApplications, nextCourses] = await Promise.all([
        getCourseSignups(activeTenantId),
        getCourses(activeTenantId),
      ]);
      setApplications(nextApplications);
      setCourses(nextCourses);
    } catch (error) {
      console.error(error);
      setApplications([]);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [activeTenantId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  const courseLookup = useMemo(
    () => new Map(courses.map((course) => [course.id, course])),
    [courses],
  );

  const activeApplications = useMemo(
    () => applications.filter((item) => normalizeText(item.status) !== 'student'),
    [applications],
  );

  const uniqueCourses = useMemo(() => Array.from(new Set(
    activeApplications
      .map((item) => item.courseName || courseLookup.get(item.courseId)?.name)
      .filter(Boolean),
  )).sort(), [activeApplications, courseLookup]);

  const uniqueCountries = useMemo(() => Array.from(new Set(
    activeApplications
      .map((item) => item.countryOfResidence || item.country || '')
      .filter(Boolean),
  )).sort(), [activeApplications]);

  const filteredApplications = useMemo(() => {
    const search = normalizeText(searchTerm);

    return activeApplications.filter((item) => {
      const courseName = item.courseName || courseLookup.get(item.courseId)?.name || '';
      const country = item.countryOfResidence || item.country || '';
      const matchesSearch = !search || [
        formatStudentName(item),
        item.email,
        courseName,
        country,
        item.phone,
        item.intakeLabel,
      ].some((value) => normalizeText(value).includes(search));
      const matchesCourse = filterCourse === 'All' || courseName === filterCourse;
      const matchesCountry = filterCountry === 'All' || country === filterCountry;
      const matchesLead = filterLead === 'All' || normalizeLeadStatus(item.leadStatus) === normalizeText(filterLead);
      return matchesSearch && matchesCourse && matchesCountry && matchesLead;
    });
  }, [activeApplications, courseLookup, filterCountry, filterCourse, filterLead, searchTerm]);

  const admissionsSnapshot = useMemo(() => {
    const today = new Date();
    const newToday = activeApplications.filter((item) => sameDay(toDate(item.createdAt), today)).length;
    const hot = activeApplications.filter((item) => normalizeLeadStatus(item.leadStatus) === 'hot').length;
    const needsAttention = activeApplications.filter((item) => normalizeLeadStatus(item.leadStatus) === 'neutral').length;
    const admitted = applications.filter((item) => normalizeText(item.status) === 'student').length;
    const conversion = applications.length ? Math.round((admitted / applications.length) * 100) : 0;
    return { admitted, conversion, hot, needsAttention, newToday };
  }, [activeApplications, applications]);

  const courseBreakdown = useMemo(() => {
    const totals = new Map();
    activeApplications.forEach((item) => {
      const courseName = item.courseName || courseLookup.get(item.courseId)?.name || 'Course not set';
      totals.set(courseName, (totals.get(courseName) || 0) + 1);
    });
    return Array.from(totals.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);
  }, [activeApplications, courseLookup]);

  const weeklyActivity = useMemo(() => {
    const formatter = new Intl.DateTimeFormat('en-US', { weekday: 'short' });
    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date();
      date.setHours(0, 0, 0, 0);
      date.setDate(date.getDate() - (6 - index));
      const count = activeApplications.filter((item) => sameDay(toDate(item.createdAt), date)).length;
      return { count, label: formatter.format(date).slice(0, 2) };
    });
  }, [activeApplications]);

  const maxDailyActivity = Math.max(1, ...weeklyActivity.map((day) => day.count));
  const filteredCount = filteredApplications.length;
  const filtersActive = searchTerm || filterCourse !== 'All' || filterCountry !== 'All' || filterLead !== 'All';
  const todayLabel = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date());

  function clearFilters() {
    setSearchTerm('');
    setFilterCourse('All');
    setFilterCountry('All');
    setFilterLead('All');
  }

  async function updateLeadStatus(item) {
    if (!activeTenantId || !item?.id) return;

    const nextStatus = getNextLeadStatus(item.leadStatus);
    setSavingLeadId(item.id);
    try {
      const updatedAt = new Date().toISOString();
      await updateCourseSignup(activeTenantId, item.id, {
        leadStatus: nextStatus,
        leadStatusUpdatedAt: updatedAt,
      });
      setApplications((current) => current.map((application) => (
        application.id === item.id
          ? { ...application, leadStatus: nextStatus, leadStatusUpdatedAt: updatedAt }
          : application
      )));
    } catch (error) {
      console.error('Unable to update lead status', error);
    } finally {
      setSavingLeadId('');
    }
  }

  return (
    <div className="admissions-command-page">
      <header className="admissions-command-header">
        <div>
          <p className="admissions-command-eyebrow"><Sparkles size={13} /> Admissions workspace</p>
          <h1>Applications</h1>
          <p>Review every enquiry, spot high-intent learners, and move the right people forward.</p>
        </div>
        <div className="admissions-command-date">
          <CalendarDays size={16} />
          <span><small>Today</small>{todayLabel}</span>
        </div>
      </header>

      <section className="admissions-metric-grid" aria-label="Admissions overview">
        <article className="admissions-metric-card admissions-metric-card--dark">
          <span className="admissions-metric-icon"><UsersRound size={18} /></span>
          <div>
            <small>Active applications</small>
            <strong>{activeApplications.length}</strong>
            <p>People waiting in your admissions pipeline.</p>
          </div>
          <ArrowUpRight size={17} />
        </article>
        <article className="admissions-metric-card">
          <span className="admissions-metric-icon admissions-metric-icon--orange"><CalendarDays size={18} /></span>
          <div>
            <small>Arrived today</small>
            <strong>{admissionsSnapshot.newToday}</strong>
            <p>New conversations ready to begin.</p>
          </div>
        </article>
        <article className="admissions-metric-card">
          <span className="admissions-metric-icon admissions-metric-icon--rose"><Flame size={18} /></span>
          <div>
            <small>High-intent leads</small>
            <strong>{admissionsSnapshot.hot}</strong>
            <p>Applicants currently marked as hot.</p>
          </div>
        </article>
        <article className="admissions-metric-card">
          <span className="admissions-metric-icon admissions-metric-icon--green"><UserCheck size={18} /></span>
          <div>
            <small>Admitted learners</small>
            <strong>{admissionsSnapshot.admitted}</strong>
            <p>{admissionsSnapshot.conversion}% of all recorded enquiries.</p>
          </div>
        </article>
      </section>

      <div className="admissions-workspace-grid">
        <section className="admissions-records-card">
          <div className="admissions-records-heading">
            <div>
              <span>Application queue</span>
              <h2>People to review</h2>
            </div>
            <p>{filteredCount} result{filteredCount === 1 ? '' : 's'}</p>
          </div>

          <div className="admissions-toolbar">
            <label className="admissions-search" htmlFor="applications-search">
              <Search size={16} aria-hidden="true" />
              <input
                id="applications-search"
                type="search"
                placeholder="Search name, course or phone"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </label>
            <select aria-label="Filter by course" value={filterCourse} onChange={(event) => setFilterCourse(event.target.value)}>
              <option value="All">All courses</option>
              {uniqueCourses.map((course) => <option key={course} value={course}>{course}</option>)}
            </select>
            <select aria-label="Filter by country" value={filterCountry} onChange={(event) => setFilterCountry(event.target.value)}>
              <option value="All">All countries</option>
              {uniqueCountries.map((country) => <option key={country} value={country}>{country}</option>)}
            </select>
          </div>

          <div className="admissions-segment-row" aria-label="Filter applications by lead status">
            {['All', 'Hot', 'Neutral', 'Cold'].map((status) => (
              <button
                type="button"
                key={status}
                className={filterLead === status ? 'is-active' : ''}
                onClick={() => setFilterLead(status)}
              >
                {status === 'Neutral' ? 'Needs attention' : status}
              </button>
            ))}
            {filtersActive ? <button type="button" className="admissions-clear-filter" onClick={clearFilters}>Clear filters</button> : null}
          </div>

          <div className="admissions-table-wrap">
            <table className="admissions-table">
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>Course &amp; intake</th>
                  <th>Location</th>
                  <th>Submitted</th>
                  <th>Lead</th>
                  <th><span className="sr-only">Open</span></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="admissions-table-message"><span className="admissions-inline-loader" /> Loading your applications…</td></tr>
                ) : filteredApplications.length ? filteredApplications.map((item) => {
                  const studentName = formatStudentName(item);
                  const courseName = item.courseName || courseLookup.get(item.courseId)?.name || 'Course not set';
                  const country = item.countryOfResidence || item.country || 'Not provided';
                  const status = item.leadStatus || 'Neutral';

                  return (
                    <tr key={item.id}>
                      <td>
                        <div className="admissions-applicant-cell">
                          <span className="admissions-applicant-avatar" aria-hidden="true">{getInitials(studentName)}</span>
                          <span>
                            <strong>{studentName}</strong>
                            <small>{item.email || item.phone || 'No contact details'}</small>
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="admissions-primary-value">{courseName}</span>
                        <small className="admissions-secondary-value">{item.intakeLabel || 'Open intake'}</small>
                      </td>
                      <td><span className="admissions-location"><MapPin size={13} />{country}</span></td>
                      <td><span className="admissions-primary-value">{formatDateTime(toDate(item.createdAt) || item.createdAt)}</span></td>
                      <td>
                        <button
                          type="button"
                          className={`admissions-lead-pill admissions-lead-pill--${statusTone(status)}`}
                          onClick={() => updateLeadStatus(item)}
                          disabled={savingLeadId === item.id}
                          title="Click to change lead status"
                        >
                          <CircleDot size={12} />
                          {savingLeadId === item.id ? 'Saving…' : status}
                        </button>
                      </td>
                      <td>
                        <Link className="admissions-review-link" to={`/view-student?applicationId=${item.id}`} aria-label={`Review ${studentName}'s application`}>
                          Review <ChevronRight size={14} />
                        </Link>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan="6" className="admissions-empty-state">
                      <span><Search size={19} /></span>
                      <strong>{filtersActive ? 'No applications match these filters' : 'Your application queue is clear'}</strong>
                      <p>{filtersActive ? 'Try a broader search or clear the filters to see every applicant.' : 'New applicants will appear here as soon as they submit an application.'}</p>
                      {filtersActive ? <button type="button" onClick={clearFilters}>Clear filters</button> : null}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="admissions-insights-rail">
          <section className="admissions-pulse-card">
            <div className="admissions-rail-heading">
              <span>Admissions pulse</span>
              <Sparkles size={15} />
            </div>
            <div className="admissions-conversion">
              <div className="admissions-conversion-ring" style={{ '--admissions-progress': `${admissionsSnapshot.conversion * 3.6}deg` }}>
                <span><strong>{admissionsSnapshot.conversion}%</strong><small>conversion</small></span>
              </div>
              <p>From recorded enquiry to admitted learner.</p>
            </div>
            <div className="admissions-pulse-list">
              <div><span><i className="is-orange" />Needs attention</span><strong>{admissionsSnapshot.needsAttention}</strong></div>
              <div><span><i className="is-rose" />High intent</span><strong>{admissionsSnapshot.hot}</strong></div>
              <div><span><i className="is-green" />Admitted</span><strong>{admissionsSnapshot.admitted}</strong></div>
            </div>
          </section>

          <section className="admissions-activity-card">
            <div className="admissions-rail-heading">
              <span>Last 7 days</span>
              <CalendarDays size={15} />
            </div>
            <div className="admissions-mini-chart" aria-label="Applications received in the last seven days">
              {weeklyActivity.map((day, index) => (
                <div key={`${day.label}-${index}`}>
                  <span className={index === weeklyActivity.length - 1 ? 'is-today' : ''} style={{ height: `${Math.max(day.count ? 24 : 7, (day.count / maxDailyActivity) * 84)}%` }} title={`${day.count} application${day.count === 1 ? '' : 's'}`} />
                  <small>{day.label}</small>
                </div>
              ))}
            </div>
            <p><strong>{weeklyActivity.reduce((total, day) => total + day.count, 0)}</strong> applications arrived this week.</p>
          </section>

          <section className="admissions-interest-card">
            <div className="admissions-rail-heading">
              <span>Course interest</span>
              <BookOpen size={15} />
            </div>
            {courseBreakdown.length ? (
              <div className="admissions-course-interest-list">
                {courseBreakdown.map((course) => (
                  <div key={course.name}>
                    <span title={course.name}>{course.name}</span>
                    <strong>{course.count}</strong>
                  </div>
                ))}
              </div>
            ) : <p className="admissions-no-insight">Course interest will appear as applications arrive.</p>}
          </section>

          <section className="admissions-focus-card">
            <span><CheckCircle2 size={17} /></span>
            <div><small>Today’s focus</small><strong>Follow up while interest is fresh.</strong></div>
            <ArrowUpRight size={15} />
          </section>
        </aside>
      </div>
    </div>
  );
}
